export interface ComingSoonPage {
  path: string
  eyebrow: string
  h1: string
  paragraph: string
  title: string
  description: string
}

export const comingSoonPages: ComingSoonPage[] = [
  {
    path: '/products/',
    eyebrow: 'Products',
    h1: 'Shear Geometry Products',
    paragraph:
      'Detailed product information is not yet published on this page. The homepage summarizes the legacy catalog; call 800-397-7745 or email sales@sheargeometry.com to confirm current availability and application fit.',
    title: 'Shear Geometry Products | Robertson Precision',
    description:
      'Detailed Shear Geometry product information is not yet published. Contact Robertson Precision at 800-397-7745 to confirm current availability and application fit.',
  },
  {
    path: '/products/starter-kits/',
    eyebrow: 'Products',
    h1: 'Starter Kits',
    paragraph:
      'Current kit details are not yet published on this page. The legacy catalog paired a 1-inch or 3/4-inch holder with 10 APGX 331R inserts, a T-15 Torx driver and extra screws. Confirm the present configuration and availability directly.',
    title: 'Shear Geometry Starter Kits | Robertson Precision',
    description:
      'Shear Geometry starter kit details are not yet published. The legacy catalog paired a 1-inch or 3/4-inch holder with 10 APGX 331R inserts, a driver and extra screws.',
  },
  {
    path: '/products/holders/',
    eyebrow: 'Products',
    h1: 'Tool Holders',
    paragraph:
      'Current holder dimensions and compatibility drawings are not yet published on this page. The legacy catalog lists SALXR 16-3D with a 1-inch shank and SALXR 12-3B with a 3/4-inch shank. Confirm the present models and availability directly.',
    title: 'Shear Geometry Tool Holders | Robertson Precision',
    description:
      'Holder dimensions and compatibility drawings are not yet published. The legacy catalog lists SALXR 16-3D (1-inch shank) and SALXR 12-3B (3/4-inch shank).',
  },
  {
    path: '/products/inserts/',
    eyebrow: 'Products',
    h1: 'Inserts & Chipbreakers',
    paragraph:
      'Current grade, nose-radius and chipbreaker compatibility details are not yet published on this page. The homepage transcribes the legacy APGX catalog; confirm present packaging and availability directly.',
    title: 'APGX Inserts & Chipbreakers | Robertson Precision',
    description:
      'Grade, nose-radius and chipbreaker compatibility details are not yet published. The homepage transcribes the legacy APGX catalog for reference.',
  },
  {
    path: '/products/accessories/',
    eyebrow: 'Products',
    h1: 'Accessories',
    paragraph:
      'Current accessory specifications are not yet published on this page. The legacy catalog lists a T-15 Torx driver and 4-15 low-profile insert screws. Confirm present availability directly.',
    title: 'Shear Geometry Accessories | Robertson Precision',
    description:
      'Accessory specifications are not yet published. The legacy catalog lists a T-15 Torx driver and 4-15 low-profile insert screws.',
  },
  {
    path: '/applications/',
    eyebrow: 'Applications',
    h1: 'Applications',
    paragraph:
      'Current material-specific guidance is not yet published on this page. The homepage shows legacy references for 6061-T6 aluminum, copper and soft aluminum alloys, and titanium; obtain current technical confirmation before use.',
    title: 'Applications | Robertson Precision',
    description:
      'Material-specific Shear Geometry guidance is not yet published. Legacy references cover 6061-T6 aluminum, copper and soft aluminum alloys, and titanium.',
  },
  {
    path: '/applications/aluminum/',
    eyebrow: 'Applications',
    h1: 'Aluminum Applications',
    paragraph:
      'Current 6061-T6 roughing and chip-control guidance is not yet published on this page. The homepage preserves the original 20 hp CNC-lathe context for the legacy values; obtain technical confirmation before use.',
    title: 'Aluminum Applications | Robertson Precision',
    description:
      '6061-T6 roughing and chip-control guidance is not yet published. The homepage preserves the original 20 hp CNC-lathe context for the legacy values.',
  },
  {
    path: '/applications/copper-soft-alloys/',
    eyebrow: 'Applications',
    h1: 'Copper & Soft-Alloy Applications',
    paragraph:
      'Current copper and soft-alloy guidance is not yet published on this page. The homepage preserves the legacy oxygen-free-copper peck values and their source limitation; obtain technical confirmation before use.',
    title: 'Copper & Soft-Alloy Applications | Robertson Precision',
    description:
      'Copper and soft-alloy guidance is not yet published. The homepage preserves the legacy oxygen-free-copper peck values and their source limitation.',
  },
  {
    path: '/applications/titanium/',
    eyebrow: 'Applications',
    h1: 'Titanium Applications',
    paragraph:
      'Current DR80 titanium guidance is not yet published on this page. The homepage transcribes the legacy 100–200 SFM and .003–.007 IPR range and notes that its test conditions were not supplied.',
    title: 'Titanium Applications | Robertson Precision',
    description:
      'DR80 titanium guidance is not yet published. The homepage transcribes the legacy 100–200 SFM and .003–.007 IPR starting range.',
  },
  {
    path: '/technical-resources/',
    eyebrow: 'Technical Resources',
    h1: 'Technical Resources',
    paragraph:
      'Current starting-data, tool-selection and safety resources are not yet published on this page. Review the qualified legacy references on the homepage and contact Robertson Precision for confirmation.',
    title: 'Technical Resources | Robertson Precision',
    description:
      'Starting-data, tool-selection and safety resources are not yet published. Review the qualified legacy references on the homepage.',
  },
  {
    path: '/technical-resources/starting-data/',
    eyebrow: 'Technical Resources',
    h1: 'Starting Data',
    paragraph:
      'Current feeds, speeds, depth-of-cut values and complete test conditions are not yet published on this page. The homepage data is legacy reference material and requires Robertson Precision’s technical approval before use.',
    title: 'Starting Data | Robertson Precision',
    description:
      'Feeds, speeds, depth-of-cut values and complete test conditions are not yet published. Homepage data is legacy reference material pending technical approval.',
  },
  {
    path: '/technical-resources/tool-selection/',
    eyebrow: 'Technical Resources',
    h1: 'Tool Selection',
    paragraph:
      'A current holder-and-insert selection guide is not yet published on this page. Send the material, machine, horsepower, workholding, holder size and application goal to confirm available options.',
    title: 'Tool Selection | Robertson Precision',
    description:
      'A holder-and-insert selection guide is not yet published. Send the material, machine, horsepower, workholding and holder size to confirm options.',
  },
  {
    path: '/technical-resources/safety/',
    eyebrow: 'Technical Resources',
    h1: 'Setup & Safety',
    paragraph:
      'Current setup and safety documentation is not yet published on this page. The legacy instructions specify CNC lathes only; review the homepage cautions and follow all machine, insert and shop procedures.',
    title: 'Setup & Safety | Robertson Precision',
    description:
      'Setup and safety documentation is not yet published. The legacy instructions specify CNC lathes only; follow all machine, insert and shop procedures.',
  },
  {
    path: '/about/',
    eyebrow: 'Company',
    h1: 'About Robertson Precision',
    paragraph:
      'A verified company and Shear Geometry development timeline is not yet published on this page. Any future account must distinguish historical machining work from confirmed present operations.',
    title: 'About Robertson Precision | Shear Geometry Tooling',
    description:
      'A verified company and Shear Geometry development timeline is not yet published on this page.',
  },
  {
    path: '/contact/',
    eyebrow: 'Company',
    h1: 'Contact Robertson Precision',
    paragraph:
      'Full contact details are not yet published on this page. To ask about Robertson Precision or Shear Geometry, call 800-397-7745 or email sales@sheargeometry.com. Mailing address: 325 Sharon Park Drive, Unit #444, Menlo Park, CA 94025.',
    title: 'Contact Robertson Precision | Shear Geometry Tooling',
    description:
      'Call Robertson Precision at 800-397-7745 or email sales@sheargeometry.com with Shear Geometry product and application questions.',
  },
  {
    path: '/where-to-buy/',
    eyebrow: 'Products',
    h1: 'Where to Buy',
    paragraph:
      'Current dealer and ordering information is not yet published on this page. Contact Robertson Precision directly at 800-397-7745 or sales@sheargeometry.com.',
    title: 'Where to Buy | Robertson Precision',
    description:
      'Dealer and ordering information is not yet published. Contact Robertson Precision at 800-397-7745 or sales@sheargeometry.com.',
  },
  {
    path: '/company-history/',
    eyebrow: 'Company',
    h1: 'Company History',
    paragraph:
      'A sourced company timeline is not yet published on this page. Any future history will label the former machining operation clearly and keep it separate from confirmed present product availability.',
    title: 'Company History | Robertson Precision',
    description:
      'A sourced Robertson Precision company timeline is not yet published on this page.',
  },
  {
    path: '/historic-work/',
    eyebrow: 'Company',
    h1: 'Historic Work',
    paragraph:
      'Historic project imagery is not yet published on this page. Any future archive will require permission and clear historical labeling so it is not mistaken for current production capability.',
    title: 'Historic Work | Robertson Precision',
    description:
      'Historic project imagery is not yet published on this page. Any future archive will carry clear historical labeling.',
  },
  {
    path: '/careers/',
    eyebrow: 'Company',
    h1: 'Careers',
    paragraph:
      'No current openings are published. Employment information will appear here when Robertson Precision has an active role to announce.',
    title: 'Careers | Robertson Precision',
    description:
      'No current openings are published. Employment information will appear here when Robertson Precision has an active role to announce.',
  },
  {
    path: '/privacy/',
    eyebrow: 'Legal',
    h1: 'Privacy',
    paragraph:
      'A Robertson Precision privacy notice is not yet published. The product-guidance form opens the visitor’s own email application and does not transmit form data to a website backend.',
    title: 'Privacy | Robertson Precision',
    description:
      'A Robertson Precision privacy notice is not yet published. The product-guidance form opens the visitor’s own email application.',
  },
  {
    path: '/cookie-policy/',
    eyebrow: 'Legal',
    h1: 'Cookie Policy',
    paragraph:
      'A Robertson Precision-specific cookie notice is not yet published. Do not add nonessential cookies or analytics until an approved notice and consent approach are in place.',
    title: 'Cookie Policy | Robertson Precision',
    description:
      'A Robertson Precision-specific cookie notice is not yet published on this page.',
  },
]
